import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { ImageIcon, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChangelogEditor } from "@/components/changelog/changelog-editor";
import { useImageUpload } from "@/hooks/use-image-upload";
import type { ChangelogEntryFormValues } from "@/lib/validation/changelog-schema";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

interface ChangelogEditorPanelProps {
  lang: "ko" | "en";
}

export function ChangelogEditorPanel({ lang }: ChangelogEditorPanelProps) {
  const form = useFormContext<ChangelogEntryFormValues>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const featuredImageUrl = form.watch("featuredImageUrl");

  const { upload, isUploading } = useImageUpload({
    category: "changelog-featured",
  });

  const { upload: uploadBodyImage } = useImageUpload({
    category: "changelog-body",
  });

  const handleBodyImageUpload = async (file: File): Promise<string> => {
    const url = await uploadBodyImage(file);
    if (!url) throw new Error("이미지 업로드에 실패했습니다");
    return url;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await upload(file);
    if (url) {
      form.setValue("featuredImageUrl", url, { shouldValidate: true });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClearImage = () => {
    form.setValue("featuredImageUrl", "", { shouldValidate: true });
  };

  const titleField = lang === "ko" ? "titleKo" : "titleEn";
  const bodyField = lang === "ko" ? "bodyKo" : "bodyEn";
  const titlePlaceholder =
    lang === "ko" ? "제목 없는 체인지로그" : "Untitled changelog";
  const bodyPlaceholder =
    lang === "ko"
      ? "/ 를 입력하여 옵션을 탐색하세요"
      : "Type / to browse options";

  return (
    <div className="h-full overflow-auto bg-white dark:bg-white">
      <div className="mx-auto max-w-[900px] py-8 pl-14 pr-6">
        {/* Featured Image */}
        {featuredImageUrl ? (
          <div className="relative mb-8 overflow-hidden rounded-lg border border-border/50">
            <img
              src={featuredImageUrl}
              alt="Featured"
              className="max-h-[400px] w-full object-contain"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-3 right-3 h-7 w-7"
              onClick={handleClearImage}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            disabled={isUploading}
            className="mb-8 flex h-40 w-full items-center justify-center rounded-lg border border-dashed border-muted-foreground/25 transition-colors hover:border-muted-foreground/50 hover:bg-muted/30 disabled:cursor-not-allowed"
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">업로드 중...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-1.5 text-muted-foreground">
                <ImageIcon className="h-4 w-4" />
                <span className="text-sm">
                  {lang === "ko" ? "대표 이미지 추가" : "Add featured image"}
                </span>
              </div>
            )}
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={handleFileSelect}
        />

        {/* Title */}
        <input
          type="text"
          value={form.watch(titleField)}
          onChange={(e) =>
            form.setValue(titleField, e.target.value, { shouldValidate: true })
          }
          placeholder={titlePlaceholder}
          className="mb-1 w-full bg-transparent pr-4 text-3xl font-bold text-foreground placeholder:text-muted-foreground/50 focus:outline-none"
        />

        {/* Body Editor */}
        <ChangelogEditor
          key={lang}
          content={form.watch(bodyField)}
          onChange={(html) =>
            form.setValue(bodyField, html, { shouldValidate: true })
          }
          placeholder={bodyPlaceholder}
          onImageUpload={handleBodyImageUpload}
        />
      </div>
    </div>
  );
}
